/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Schema, ValidationError } from '@squiz/component-editing-ui';
import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  Button,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_SUCCESS,
} from '@squiz/sds';
import { JsonEditor } from 'apps/console-core/src/Components/JsonEditor/JsonEditor';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPermission } from '../../../../console-core/src/Utilities/HasPermission/HasPermission';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { useContextItemPersister } from '../../Services/ai/src/Hooks';
import { AiContextItemDialogProps } from './AiContextItemDialog.props';

export const AiContextItemDialog = (props: AiContextItemDialogProps): JSX.Element => {
  const { ai, schema, contextId, contextItem, clearCache, setButtons, setButtonKeys, close } = props;
  const { activeDialog, updateActiveDialog } = useDialogStore();
  const editable = hasPermission(PERMISSIONS.dxpAi.CONTEXT_INSTANCE_WRITE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { draft, setDraft, isSaving, save } = useContextItemPersister({
    ai,
    contextId,
    contextItem,
    onSaveError: useCallback(
      (error) => {
        updateActiveDialog({
          ...activeDialog,
          errorMessage: error instanceof Error && error.message,
        });
      },
      [activeDialog, updateActiveDialog],
    ),
    onSaveSuccess: useCallback(() => {
      clearCache();
      close();
    }, [clearCache, close]),
  });
  const { t } = useTranslation();
  const labels = useMemo(
    () => ({
      cancel: t(AI_STRINGS.cancel),
      create: t(AI_STRINGS.create),
      save: t(AI_STRINGS.save),
    }),
    [t],
  );
  const handleValidate = (errors: Array<ValidationError>): void => {
    setIsSubmitting(false);

    if (errors.length === 0) {
      save();
    }
  };

  const handleInputChange = useCallback(
    (data: unknown) => {
      setDraft((previous) => ({ ...previous, data }));
    },
    [setDraft],
  );

  useEffect(() => {
    const buttons: Record<string, JSX.Element> = {
      cancel: (
        <Button
          data-id="Cancel Context Item Button"
          format={BUTTON_FORMAT_SOFT}
          label={labels.cancel}
          onClick={close}
          text={labels.cancel}
          theme={BUTTON_THEME_DEFAULT}
        />
      ),
    };

    if (editable) {
      buttons.save = (
        <Button
          data-id="Save Context Item Button"
          format={BUTTON_FORMAT_STRONG}
          isLoading={isSaving}
          label={contextItem ? labels.save : labels.create}
          onClick={() => setIsSubmitting(true)}
          text={contextItem ? labels.save : labels.create}
          theme={BUTTON_THEME_SUCCESS}
        />
      );
    }

    setButtonKeys(Object.keys(buttons));
    setButtons(Object.values(buttons));
  }, [close, contextItem, editable, isSaving, labels, save, setButtonKeys, setButtons]);

  return (
    <JsonEditor
      data={draft.data}
      disabled={!editable}
      onValidate={handleValidate}
      schema={schema as Schema}
      setData={handleInputChange}
      showDescriptions
      validate={isSubmitting}
    />
  );
};
